import { ReactElement, ReactNode, useCallback, useEffect, useState, Suspense, memo, useMemo } from 'react';

import { useNavigate } from 'react-router-dom';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import SvgIcon from '@mui/material/SvgIcon';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';

import { ActionProps, useApp } from '../../contextProviders/AppProvider';
import { useAuth } from '../../contextProviders/AuthProvider';
import { useData } from '../../contextProviders/DataProvider';
import Person from '../../types/Person';

import GratiActionBar, { ResponsiveTitle } from './GratiActionBar';
import GratiActionBarContainer from './GratiActionBarContainer';
import GratiGodActions from './GratiGodActions';
import GratiSystemError from './GratiSystemError';
import Hourglass from './Hourglass';

export interface WrapperAction {
  title?: string | ResponsiveTitle;
  actions?: ReactElement | null;
}

interface GratiPageWrapperProps {
  children: ReactNode;
  isContentLoading?: boolean;
  isAuthRequired?: boolean;
  isNoAuthRequired?: boolean;
  validateUser?: (userProfile: Person | null | undefined) => boolean;
  pageActions?: ActionProps[] | WrapperAction | ReactElement | null;
  godActions?: WrapperAction | ReactElement | null;
  error?: Error | null;
  contentError?: Error | null;
  onErrorRetry?: () => void;
}

const MemoizedGratiGodActions = memo(GratiGodActions);

function isWrapperAction(value: WrapperAction | ReactElement | null | undefined): value is WrapperAction {
  if (!value) return false;
  return typeof value === 'object' && !('type' in value && 'props' in value);
}

function normalizeAction(value: WrapperAction | ReactElement | null | undefined): WrapperAction {
  if (!value) return {};
  if (isWrapperAction(value)) return value;
  return { actions: value };
}

export default function GratiPageWrapper(props: GratiPageWrapperProps) {
  const { children, validateUser, error, contentError, onErrorRetry } = props;
  const isNoAuthRequired = props.isNoAuthRequired ?? false;
  const isAuthRequired = props.isAuthRequired ?? !isNoAuthRequired;
  const isContentLoading = props.isContentLoading ?? false;

  const navigate = useNavigate();
  const { updateActions, updatePageActions } = useApp();
  const { isAuthGod } = useAuth();
  const { userState } = useData();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  const memoizedUserProfile = useMemo(() => {
    return userState.status === 'ready' ? userState.userProfile : null;
  }, [userState.status, userState.userProfile]);

  const [isWrapperLoading, setIsWrapperLoading] = useState(true);
  const [isPageReady, setIsPageReady] = useState(false);

  // Detect new ActionProps[] format vs old WrapperAction/ReactElement format
  const isNewFormat = Array.isArray(props.pageActions);
  const newPageActions = isNewFormat ? (props.pageActions as ActionProps[]) : null;
  const godAction = normalizeAction(props.godActions as WrapperAction | ReactElement | null | undefined);

  // Old format normalization (only used when not ActionProps[])
  const pageAction = isNewFormat ? ({} as WrapperAction) : normalizeAction(props.pageActions as WrapperAction | ReactElement | null | undefined);

  // Push ActionProps[] to AppBar page slot on small screens
  useEffect(() => {
    if (!isNewFormat || !newPageActions) return;
    if (isSmallScreen) {
      updatePageActions(newPageActions);
    } else {
      updatePageActions([]);
    }
  }, [isNewFormat, newPageActions, isSmallScreen, updatePageActions]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      updateActions([]);
      updatePageActions([]);
    };
  }, [updateActions, updatePageActions]);

  useEffect(() => {
    setIsWrapperLoading(true);
  }, []);

  useEffect(() => {
    if (userState.status === 'ready' || userState.status === 'logged_out') {
      if (memoizedUserProfile) {
        if (validateUser && !validateUser(memoizedUserProfile)) {
          navigate('/');
        }
        if (isNoAuthRequired) {
          navigate('/');
        }
        setIsWrapperLoading(false);
      } else {
        if (isAuthRequired) {
          navigate('/login');
        }
        setIsWrapperLoading(false);
      }
    }
  }, [memoizedUserProfile, userState.status, validateUser, navigate, isAuthRequired, isNoAuthRequired]);

  useEffect(() => {
    if (!isWrapperLoading && !isContentLoading) {
      setIsPageReady(true);
    }
  }, [isWrapperLoading, isContentLoading]);

  const renderNewPageActionsBar = useCallback((actions: ActionProps[]) => (
    <Stack direction="row" spacing={1}>
      {actions.map((action) => (
        <Button
          key={action.name}
          onClick={action.handleClick}
          variant={action.variant !== 'icon' ? (action.variant ?? 'text') : undefined}
          size="small"
          type={action.type ?? 'button'}
          form={action.form}
          disabled={action.disabled}
          color={action.color}
          startIcon={action.iconElement ?? (action.icon ? <SvgIcon component={action.icon} /> : undefined)}
        >
          {action.name}
        </Button>
      ))}
    </Stack>
  ), []);

  if (error) {
    return (
      <Box>
        <Typography component="h6" color="error">
          {error.message}
        </Typography>
      </Box>
    );
  }

  if (contentError) {
    return <GratiSystemError error={contentError} onRetry={onErrorRetry} />;
  }

  if (!isPageReady) {
    return <Hourglass />;
  }

  // Determine whether to show the large-screen striped action bar
  const hasLargeScreenPageActions = isNewFormat
    ? (!isSmallScreen && newPageActions != null && newPageActions.length > 0)
    : !!(pageAction.title || pageAction.actions);
  const hasBottomActions = godAction.title || godAction.actions || isAuthGod;

  return (
    <Suspense fallback={<Hourglass />}>
      <>
        {hasLargeScreenPageActions && (
          <GratiActionBarContainer position="top" sx={{ mb: 2 }}>
            {isNewFormat && newPageActions ? (
              <GratiActionBar actions={renderNewPageActionsBar(newPageActions)} variant="top" />
            ) : (
              <GratiActionBar title={pageAction.title} actions={pageAction.actions} variant="top" />
            )}
          </GratiActionBarContainer>
        )}
        {children}
        {hasBottomActions && (
          <GratiActionBarContainer position="bottom" sx={{ mt: 2 }}>
            <GratiActionBar title={godAction.title} actions={godAction.actions} variant="bottom" />
            <GratiActionBar actions={<MemoizedGratiGodActions />} variant="god" />
          </GratiActionBarContainer>
        )}
      </>
    </Suspense>
  );
}
